import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertCircle, Shield, Mail, Chrome, Loader2 } from 'lucide-react'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false)
  const [error, setError] = useState('')
  const [showEmailForm, setShowEmailForm] = useState(false)

  const { user, login, loginWithGoogle, loginWithMicrosoft } = useAuth()

  if (user) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      await login(email, password)
    } catch (err: any) {
      console.error('Login error:', err)
      
      // Handle specific Firebase error codes
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address')
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password')
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address')
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later')
      } else if (err.message?.includes('Admin privileges required')) {
        setError('Access denied: This account does not have administrator privileges. Please contact your system administrator.')
      } else {
        setError('Login failed. Please check your credentials and try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Shared error mapping for the social buttons. Identical for every provider
   * except the label, and the admin-privileges case is the one that actually
   * fires in practice: a valid Microsoft/Google sign-in that simply is not an
   * admin fails here, not at the provider.
   */
  const describeSocialError = (err: any, providerLabel: string): string => {
    if (err.message?.includes('Admin privileges required') || err.message?.includes('Access denied')) {
      return `Access denied: This ${providerLabel} account does not have administrator privileges. Only authorized administrators can access this panel.`
    }
    switch (err.code) {
      case 'auth/popup-closed-by-user':
        return 'Sign-in cancelled. Please try again.'
      case 'auth/popup-blocked':
        return 'Pop-up blocked. Please allow pop-ups for this site and try again.'
      case 'auth/cancelled-popup-request':
        return 'Another sign-in attempt in progress. Please wait.'
      case 'auth/operation-not-allowed':
        return `${providerLabel} sign-in is not enabled for this Firebase project.`
      default:
        return `${providerLabel} sign-in failed. Please try again or use email/password login.`
    }
  }

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true)
    setError('')

    try {
      await loginWithGoogle()
    } catch (err: any) {
      console.error('Google login error:', err)
      setError(describeSocialError(err, 'Google'))
    } finally {
      setIsGoogleLoading(false)
    }
  }

  const handleMicrosoftLogin = async () => {
    setIsMicrosoftLoading(true)
    setError('')

    try {
      await loginWithMicrosoft()
    } catch (err: any) {
      console.error('Microsoft login error:', err)
      setError(describeSocialError(err, 'Microsoft'))
    } finally {
      setIsMicrosoftLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo/Brand Section */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-4">
            <Shield className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Ponder Admin</h1>
          <p className="text-muted-foreground mt-1">Administrative Control Panel</p>
        </div>

        <Card>
          <CardHeader className="text-center">
            <CardTitle>Admin Login</CardTitle>
            <CardDescription>
              Sign in with your administrator credentials
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Social Login Options */}
            {!showEmailForm && (
              <div className="space-y-3">
                <Button
                  onClick={handleGoogleLogin}
                  disabled={isGoogleLoading}
                  className="w-full"
                  variant="outline"
                >
                  {isGoogleLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Chrome className="h-4 w-4 mr-2" />
                  )}
                  Continue with Google
                </Button>

                <Button
                  onClick={handleMicrosoftLogin}
                  disabled={isMicrosoftLoading}
                  className="w-full"
                  variant="outline"
                >
                  {isMicrosoftLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <svg viewBox="0 0 23 23" className="h-4 w-4 mr-2" aria-hidden="true">
                      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
                    </svg>
                  )}
                  Continue with Microsoft
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">or</span>
                  </div>
                </div>
                
                <Button
                  onClick={() => setShowEmailForm(true)}
                  variant="outline"
                  className="w-full"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Continue with Email
                </Button>
              </div>
            )}

            {/* Email/Password Form */}
            {showEmailForm && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
                    placeholder="admin@ponder.do"
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
                    placeholder="••••••••"
                    disabled={isLoading}
                  />
                </div>
                
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    'Sign In'
                  )}
                </Button>
                
                <Button
                  type="button"
                  onClick={() => setShowEmailForm(false)}
                  variant="ghost"
                  className="w-full"
                >
                  Back to login options
                </Button>
              </form>
            )}
            
            {/* Error Display */}
            {error && (
              <div className="flex items-start space-x-2 p-4 bg-destructive/10 border border-destructive/20 rounded-md animate-in fade-in-50 slide-in-from-top-1">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div className="text-destructive text-sm font-medium">{error}</div>
              </div>
            )}
            
            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                Secure access for authorized administrators only
              </p>
            </div>
          </CardContent>
        </Card>
        
        {/* Footer with Version */}
        <div className="text-center space-y-1">
          <p className="text-xs text-muted-foreground">
            © 2024 Ponder. All rights reserved.
          </p>
          <div className="text-xs text-muted-foreground/60">
            v0.0.2 • Build: {new Date().toISOString().split('T')[0]} {new Date().toTimeString().split(' ')[0]}
          </div>
          {import.meta.env.VITE_ENV && (
            <div className="text-xs text-muted-foreground/50">
              Environment: {import.meta.env.VITE_ENV}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}